/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, HStack, Text, 
  Box, useToast, Badge, Spinner,
  Icon, Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from "@chakra-ui/react";
import { 
  FiUser, FiArrowRight, FiSmartphone, FiRefreshCw
} from "react-icons/fi";
import api from "utils/api";
import { formatDate } from "utils/dateFormatter";

const ImeiGecmisiModal = ({ isOpen, onClose, aracData }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [gecmisData, setGecmisData] = useState([]);

  useEffect(() => {
    if (isOpen && aracData) {
      loadImeiGecmisi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aracData]);

  const loadImeiGecmisi = async () => {
    setLoading(true);
    
    try {
      const response = await api.get(`/api/devices/${aracData.id}/imei-gecmisi`);
      const result = await response.json();
      
      if (response.ok) {
        setGecmisData(result || []);
      } else {
        throw new Error(result.detail || "Geçmiş verisi alınamadı");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true
      });
      setGecmisData([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (durum) => {
    switch (durum) {
      case "basarili": return "green";
      case "basarisiz": return "red";
      case "beklemede": return "yellow";
      default: return "gray";
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusText = (durum) => {
    switch (durum) {
      case "basarili": return "Başarılı";
      case "basarisiz": return "Başarısız";
      case "beklemede": return "Beklemede";
      default: return "Bilinmiyor";
    }
  };

  if (!aracData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              IMEI Geçmişi
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData.plaka} • {aracData.firma} • Mevcut IMEI: {aracData.imei}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" color="blue.500" />
              <Text color="gray.600">IMEI geçmişi yükleniyor...</Text>
            </VStack>
          ) : gecmisData.length === 0 ? (
            <VStack spacing={4} py={8}>
              <Icon as={FiSmartphone} size={48} color="gray.400" />
              <VStack spacing={2}>
                <Text fontSize="lg" color="gray.600">
                  IMEI değişiklik geçmişi bulunamadı
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Bu araç için henüz IMEI değişikliği yapılmamış.
                </Text>
              </VStack>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  {gecmisData.length} kayıt bulundu
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiRefreshCw />}
                  onClick={loadImeiGecmisi}
                  isLoading={loading}
                >
                  Yenile
                </Button>
              </HStack>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>Tarih</Th>
                      <Th>IMEI Değişimi</Th>
                      <Th>Değiştiren</Th>
                      <Th>Sebep</Th>
                      <Th>Durum</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {gecmisData.map((item, index) => (
                      <Tr key={index} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <Text fontSize="sm" fontWeight="semibold">
                            {formatDate(item.degistirilme_tarihi)}
                          </Text>
                        </Td>
                        <Td>
                          <VStack spacing={1} align="start">
                            <HStack spacing={2}>
                              <Text fontSize="xs" color="red.500" fontFamily="mono" bg="red.50" px={2} py={1} borderRadius="md">
                                {item.eski_imei}
                              </Text>
                              <Icon as={FiArrowRight} color="gray.400" size={12} />
                              <Text fontSize="xs" color="green.500" fontFamily="mono" bg="green.50" px={2} py={1} borderRadius="md">
                                {item.yeni_imei}
                              </Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Icon as={FiUser} color="gray.500" size={14} />
                            <Text fontSize="sm">{item.degistiren_kullanici || "-"}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm" maxW="150px" isTruncated>
                            {item.sebep || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="green" variant="subtle">
                            Başarılı
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" onClick={onClose} size="sm">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImeiGecmisiModal;
